/**
 * KB-4351 — is this URL acceptable as a program's "Program website" (`programUrl`)?
 *
 * The TS port of `scripts/camp-data/_shared/programUrlRules.cjs`, whose authority is the adapters'
 * `adapterUtils.programPageLinks` (KB-4224). It exists because the admin forms (via the
 * `@/functions-shared` alias) and the server offer-prep seam cannot load the pipeline's `.cjs`.
 *
 * Kept in lockstep by behaviour, not by source text: both sides run `programUrlFixtures.json`, and
 * `src/utils/programUrlValidation.parity.test.ts` also asserts the two host lists are equal. The
 * portal and homepage predicates below are copies of `adapterUtils.cjs`'s; change both.
 */
import { isNonPageEndpointUrl } from "./nonPageEndpointUrl";

export type ProgramUrlProblem =
  | "not-a-url"
  | "not-http"
  | "credentials"
  | "machine-endpoint"
  | "portal"
  | "homepage"
  | "booking-vendor"
  | "vendor-listing";

/** Same list, same order, as `BOOKING_VENDOR_DOMAINS` in `programUrlRules.cjs`. */
export const BOOKING_VENDOR_DOMAINS: readonly string[] = Object.freeze([
  "ultracamp.com", "campminder.com", "campbrainregistration.com", "campbrain.com", "activenetwork.com",
  "activenetwork.net", "active.com", "activecommunities.com", "daxko.com", "daxkoengage.com", "jumbula.com",
  "hisawyer.com", "sawyer.com", "opus1.io", "jackrabbitclass.com", "amilia.com", "regpack.com", "myrec.com",
  "vermontsystems.com", "myvscloud.com", "civicrec.com", "recdesk.com", "rec1.com", "perfectmind.com",
  "capturepoint.com", "campsite.com", "campdoc.com", "enrollsy.com", "brightwheel.com", "mybrightwheel.com",
  "procaresoftware.com", "sportsengine.com", "leagueapps.com", "teamsnap.com", "iclasspro.com", "bookwhen.com",
  "mindbodyonline.com", "classmanager.com", "daysmartrecreation.com", "dashplatform.com", "pike13.com",
  "fareharbor.com", "coursestorm.com", "ezfacility.com", "learningstream.com", "bondsports.co", "zenplanner.com",
  "rockgympro.com", "roller.app", "rollerdigital.com", "clubautomation.com", "gomotionapp.com", "regfox.com",
  "webconnex.com", "hellocapitan.com", "asapconnected.com", "glofox.com", "playmetrics.com", "tessituranetwork.com",
  "app.mainstreetsites.com",
  "eventbrite.com", "signupgenius.com", "jotform.com", "typeform.com", "formstack.com", "cognitoforms.com",
  "wufoo.com", "ticketspice.com", "paypal.com", "venmo.com", "zeffy.com", "givebutter.com",
]);

export function isBookingVendorHost(host: string | null | undefined): boolean {
  const h = String(host || "").trim().toLowerCase().replace(/\.$/, "");
  if (!h) return false;
  return BOOKING_VENDOR_DOMAINS.some((d) => h === d || h.endsWith(`.${d}`));
}

/**
 * A booking vendor's search LISTING, recognised by its path because the vendor is self-hosted on a town's
 * own domain, which no host list can name. WebTrac's two skins: classic `/wbwsc/<tenant>.wsc/search.html`
 * and `/webtrac/web/search.html`. Same patterns as `VENDOR_LISTING_PATH_RES` in `programUrlRules.cjs`.
 */
const VENDOR_LISTING_PATH_RES: readonly RegExp[] = Object.freeze([
  /\.wsc\/search\.html\/?$/i,
  /\/webtrac\/web\/search\.html\/?$/i,
]);

function isVendorListingPath(pathname: string): boolean {
  return VENDOR_LISTING_PATH_RES.some((re) => re.test(pathname));
}

// ── Copied from adapterUtils.cjs (KB-1762 / KB-2795 / KB-3276): donation, account and payment portals ──
const NON_SESSION_PORTAL_SEGMENT_RE =
  /^(?:online[-_ ]?donations?|donations?|donate(?:[-_ ]?now)?|giving|give(?:[-_ ]?now)?|gifts?|pledges?|tributes?|memorials?|fund(?:raiser|raising)s?|sponsorships?|sponsor|memberships?|renewals?|renew|log[-_ ]?in|sign[-_ ]?in|signin|my[-_ ]?account|account)$/i;
const NON_SESSION_TRANSACTION_SEGMENT_RE = /^(?:carts?|checkout|payments?)$/i;
const NON_SESSION_AUTH_GATE_SEGMENT_RE = /^(?:log[-_ ]?in|sign[-_ ]?in|signin)$/i;
const AUTH_GATE_LANDING_SEGMENT_RE =
  /^(?:home|dashboard|profile|portal|index(?:\.\w+)?|welcome|default(?:\.\w+)?|start|main|settings|preferences|inbox|messages|orders|billing)$/i;
const AUTH_GATE_MAX_DECODE_LAYERS = 4;
const AUTH_GATE_REDIRECT_PARAMS = [
  "redirect_path",
  "redirect",
  "redirect_to",
  "redirect_uri",
  "redirectUrl",
  "return_to",
  "returnTo",
  "returnUrl",
  "return_url",
  "next",
  "continue",
  "destination",
];

function urlPathSegments(raw: string): string[] {
  let pathname: string;
  try {
    pathname = new URL(raw).pathname;
  } catch {
    pathname = raw.replace(/[?#].*$/, "");
  }
  return pathname
    .split("/")
    .map((seg) => seg.trim())
    .filter(Boolean);
}

function authGateRedirectTarget(raw: string): string {
  let params: URLSearchParams;
  try {
    params = new URL(raw).searchParams;
  } catch {
    const q = raw.indexOf("?");
    params = new URLSearchParams(q >= 0 ? raw.slice(q + 1).replace(/#.*$/, "") : "");
  }
  for (const name of AUTH_GATE_REDIRECT_PARAMS) {
    let v = String(params.get(name) || "").trim();
    for (let i = 0; i < AUTH_GATE_MAX_DECODE_LAYERS && /%[0-9a-f]{2}/i.test(v); i += 1) {
      let next: string;
      try {
        next = decodeURIComponent(v).trim();
      } catch {
        break;
      }
      if (next === v) break;
      v = next;
    }
    if (v) return v;
  }
  return "";
}

function resolveGateTarget(gateRaw: string, target: string): string {
  try {
    const origin = new URL(gateRaw).origin;
    const rooted = /^[a-z][a-z0-9+.-]*:|^[/\\]/i.test(target) ? target : `/${target}`;
    return new URL(rooted, origin).href;
  } catch {
    return target;
  }
}

function looksLikeGateDestination(target: string): boolean {
  const segs = urlPathSegments(target);
  return segs.length >= 2 && !AUTH_GATE_LANDING_SEGMENT_RE.test(segs[segs.length - 1]);
}

function isNonSessionUrlJudged(url: string, followGate: boolean): boolean {
  const raw = String(url || "").trim();
  if (!raw) return false;
  const segs = urlPathSegments(raw);
  for (const s of segs) {
    if (!NON_SESSION_PORTAL_SEGMENT_RE.test(s)) continue;
    if (followGate && NON_SESSION_AUTH_GATE_SEGMENT_RE.test(s)) {
      const rawTarget = authGateRedirectTarget(raw);
      const target = rawTarget ? resolveGateTarget(raw, rawTarget) : "";
      if (target && looksLikeGateDestination(target) && !isNonSessionUrlJudged(target, false)) continue;
    }
    return true;
  }
  const last = segs[segs.length - 1];
  if (last && segs.length <= 2 && NON_SESSION_TRANSACTION_SEGMENT_RE.test(last)) return true;
  return false;
}

// ── Copied from adapterUtils.cjs (KB-4224): the site's front page under any of its usual names ──
const LANGUAGE_ROOTS = "en|es|fr|de|it|pt|nl|zh|ja|ko|ru|ar|hi|vi|pl|sv|tr|he";
const HOMEPAGE_PATH_RE = new RegExp(
  `^/(?:(?:home|index|default)(?:\\.[a-z]{2,5})?|(?:${LANGUAGE_ROOTS})(?:[-_][a-z]{2})?)?/?$`,
  "i",
);
const TRACKING_PARAM_RE = /^(?:utm_[a-z]+|fbclid|gclid|msclkid|mc_cid|mc_eid|ref)$/i;
function isHomepageUrl(u: URL): boolean {
  if (!HOMEPAGE_PATH_RE.test(u.pathname || "/")) return false;
  return [...u.searchParams.keys()].every((k) => TRACKING_PARAM_RE.test(k));
}

/**
 * @returns null when `url` is acceptable as a Program website, OR blank (the field is optional);
 *   otherwise the reason it is refused.
 */
export function programUrlProblem(url: string | null | undefined): ProgramUrlProblem | null {
  const raw = String(url == null ? "" : url).trim();
  if (!raw) return null;
  let u: URL;
  try {
    u = new URL(raw);
  } catch {
    return "not-a-url";
  }
  if (!/^https?:$/.test(u.protocol)) return "not-http";
  if (u.username || u.password) return "credentials";
  if (isNonPageEndpointUrl(raw)) return "machine-endpoint";
  if (isNonSessionUrlJudged(raw, true)) return "portal";
  if (isBookingVendorHost(u.hostname)) return "booking-vendor";
  if (isVendorListingPath(u.pathname)) return "vendor-listing";
  if (isHomepageUrl(u)) return "homepage";
  return null;
}

/** Form copy for each refusal. */
export const PROGRAM_URL_PROBLEM_MESSAGES: Record<ProgramUrlProblem, string> = {
  "not-a-url": "Enter a full web address, starting with https://",
  "not-http": "Only http(s) web addresses are allowed",
  credentials: "Remove the username or password from this address",
  "machine-endpoint": "This is a data feed, not a page a parent can read",
  portal: "This is a donation, account or payment page, not the program's page",
  homepage: "This is the site's home page. Link the page that describes this program",
  "booking-vendor": "This is a registration or booking page. Put it in Registration instead",
  "vendor-listing": "This is a registration system's search results, not the program's page. Link the page that describes this program",
};

/**
 * http(s)-only check for the Session website and Registration inputs. Blank is allowed.
 * (`sanitizeAuthoredRegUrl` still runs on save for Registration; this only catches typos early.)
 */
export function isHttpUrlOrBlank(url: string | null | undefined): boolean {
  const raw = String(url == null ? "" : url).trim();
  if (!raw) return true;
  try {
    return /^https?:$/.test(new URL(raw).protocol);
  } catch {
    return false;
  }
}
